import React, { useEffect, useState, useRef, useMemo } from "react";
import "./App.css";
import { RetellWebClient } from "retell-client-js-sdk";
import { backendUrl } from "./config.ts";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import AdminPanel from "./AdminPanel";
import IframeDemo from "./IframeDemo";
import LandingPage from "./LandingPage";
import ClientLogin from "./ClientLogin";
import { MicIcon, HeadsetIcon, UserIcon, ClassicMicIcon } from "./SvgIcons";

// Remove hardcoded agentId - will get from clientConfig

interface RegisterCallResponse {
  access_token: string;
}

interface TranscriptItem {
  role: string;
  content: string;
}

interface DynamicVariablesPayload {
  [key: string]: string;
}

const retellWebClient = new RetellWebClient();

const App = () => {
  console.log('=== APP COMPONENT RENDER ===');
  console.log('Render timestamp:', Date.now());
  console.log('UNIQUE ID: NEW_VERSION_' + Date.now()); // Add unique identifier

  // Check for widget mode via URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const mode = urlParams.get("mode");
  const isWidgetMode = mode === "widget";

  // Extract demo parameters
  const hideWarnings = urlParams.get("hideWarnings") === "true";
  const showAvatar = urlParams.get("showAvatar") === "true";
  const hideTranscript = urlParams.get("hideTranscript") === "true";
  const previewTranscript = urlParams.get("previewTranscript") === "true";
  
  const [isCalling, setIsCalling] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptItem[]>([]);
  // Add state for client config
  const [clientConfig, setClientConfig] = useState<any>(null);
  const [showTranscript, setShowTranscript] = useState<boolean>(previewTranscript);
  const [isLoading, setIsLoading] = useState(true); // Add loading state
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const transcriptEndRef = useRef<HTMLDivElement | null>(null);

  const getDefaultTranscriptOpenState = () => {
    if (previewTranscript) return true;
    if (!clientConfig) return false;
    return isWidgetMode
      ? (clientConfig.ui?.transcriptDefaultOpenWidget ?? true)
      : (clientConfig.ui?.transcriptDefaultOpenFull ?? true);
  };

  // Helper function to get text color based on color setting
  const getTextColor = (colorSetting: string, customColor: string, fallback: string) => {
    if (colorSetting === 'custom') return customColor;
    if (colorSetting === 'primary') return clientConfig?.ui?.colors?.primary || fallback;
    if (colorSetting === 'secondary') return clientConfig?.ui?.colors?.secondary || fallback;
    return fallback;
  };

  // Helper function to get headline background color
  const getHeadlineBackground = () => {
    const bgType = clientConfig?.ui?.headlineBgType || 'primary';
    if (bgType === 'custom') return clientConfig?.ui?.headlineBgCustom || 'transparent';
    if (bgType === 'primary') return clientConfig?.ui?.colors?.primary || 'transparent';
    if (bgType === 'secondary') return clientConfig?.ui?.colors?.secondary || 'transparent';
    return 'transparent';
  };

  // UI style variables (must be inside component to access clientConfig)
  const transcriptBg = clientConfig?.ui?.colors?.cardBg || '#444';
  const transcriptText = clientConfig?.ui?.colors?.cardText || '#fff';
  const buttonBg = clientConfig?.ui?.colors?.buttonBg || clientConfig?.ui?.colors?.primary || '#3a4f90';
  const buttonText = '#fff';
  const micCircleBg = clientConfig?.ui?.colors?.primary || '#3a4f90';
  const glowGradient = `linear-gradient(135deg, ${clientConfig?.ui?.colors?.primary || '#3a4f90'} 0%, ${clientConfig?.ui?.colors?.secondary || '#19647e'} 100%)`;
  const accentColor = clientConfig?.ui?.colors?.secondary || '#19647e';
  const showRoleIcons = clientConfig?.ui?.showRoleIcons;

  // Extract client ID from URL parameters OR route path
  let clientId = urlParams.get("client");
  if (!clientId) {
    // Try to extract from route path (e.g., /client/45400bdwd13213A)
    const pathMatch = window.location.pathname.match(/^\/client\/(.+)$/);
    if (pathMatch) {
      clientId = pathMatch[1];
    }
  }
  
  const isStandaloneRoute = window.location.pathname === "/iframe-demo" || window.location.pathname === "/client-login";

  // Responsive/fixed sizing logic for mic circle and related elements
  const clamp = (val, min, max) => Math.max(min, Math.min(max, val));
  const responsiveMic = urlParams.get("responsiveMic") === "true";
  
  // Fallback layout when clientConfig is not loaded yet
  const fallbackLayout = { mode: 'responsive', size: 160, min: 120, max: 190 };
  const layout = clientConfig?.ui?.layout?.[isWidgetMode ? 'widget' : 'full'] || fallbackLayout;
  
  // Override layout mode if responsiveMic parameter is set
  const effectiveLayout = responsiveMic ? { ...layout, mode: 'responsive' } : layout;
  
  console.log('App.tsx - clientConfig loaded:', !!clientConfig, 'layout:', layout, 'effectiveLayout:', effectiveLayout);
  
  // Debug logging
  console.log('App.tsx - responsiveMic:', responsiveMic, 'effectiveLayout:', effectiveLayout);
  console.log('App.tsx - clientConfig.ui.layout:', clientConfig?.ui?.layout);
  console.log('App.tsx - isWidgetMode:', isWidgetMode);
  console.log('App.tsx - layout[widget]:', clientConfig?.ui?.layout?.widget);
  console.log('App.tsx - layout[widget] details:', {
    mode: clientConfig?.ui?.layout?.widget?.mode,
    size: clientConfig?.ui?.layout?.widget?.size,
    min: clientConfig?.ui?.layout?.widget?.min,
    max: clientConfig?.ui?.layout?.widget?.max
  });
  
  // Debug: Check what colors are actually being received
  console.log('App.tsx - Color values from database:', {
    primary: clientConfig?.ui?.colors?.primary,
    secondary: clientConfig?.ui?.colors?.secondary,
    background: clientConfig?.ui?.colors?.background,
    text: clientConfig?.ui?.colors?.text,
    buttonBg: clientConfig?.ui?.colors?.buttonBg,
    buttonText: clientConfig?.ui?.colors?.buttonText
  });
  
  // Validate layout values to prevent NaN
  const safeSize = Number(effectiveLayout.size) || 160;
  const safeMin = Number(effectiveLayout.min) || 120;
  const safeMax = Number(effectiveLayout.max) || 190;
  
  const micSize = effectiveLayout.mode === 'fixed' ? safeSize : clamp(window.innerWidth < 600 ? window.innerWidth * 0.7 : 160, safeMin, safeMax);
  const glowSize = micSize * (clientConfig?.ui?.glowSize || 1.2);
  const iconSize = Math.max(1, micSize * 0.4); // Reduced from 0.5 to fix fat mic issue, prevent NaN
  const avatarSize = Math.max(1, micSize * 0.875); // Prevent NaN
  
  console.log('App.tsx - micSize:', micSize, 'glowSize:', glowSize, 'iconSize:', iconSize);
  
  // Debug: Check if any values are NaN
  if (isNaN(micSize) || isNaN(iconSize) || isNaN(avatarSize)) {
    console.error('❌ NaN values detected:', { micSize, iconSize, avatarSize, effectiveLayout, layout, clientConfig: !!clientConfig });
  }

  // Apply widget mode class to body and set glow CSS variables
  useEffect(() => {
    if (isWidgetMode) {
      document.body.classList.add("widget-mode");
    } else {
      document.body.classList.remove("widget-mode");
    }
    
    // Note: Retell handles microphone permissions automatically when starting a call
    // No need for manual permission requests that can interfere with the call
    
    // Set CSS variables from client config
    if (clientConfig?.ui) {
      const glowSize = micSize * (clientConfig.ui.glowSize || 1.2);
      const glowBlur = clientConfig.ui.glowBlur || 22;
      const glowBrightness = clientConfig.ui.glowBrightness || 1.2;
      const glowOpacity = clientConfig.ui.glowOpacity || 0.8;
      const glowSpeed = clientConfig.ui.glowSpeed || 7.0;
      
      // Set the main circle size variable that controls button size
      document.documentElement.style.setProperty('--circle-size', `${micSize}px`);
      document.documentElement.style.setProperty('--glow-size', `${glowSize}px`);
      document.documentElement.style.setProperty('--glow-blur', `${glowBlur}px`);
      document.documentElement.style.setProperty('--glow-brightness', glowBrightness.toString());
      document.documentElement.style.setProperty('--glow-opacity', glowOpacity.toString());
      document.documentElement.style.setProperty('--glow-speed', `${glowSpeed}s`);
    }
  }, [isWidgetMode, clientConfig, micSize]);



  // Fetch client config on mount
  useEffect(() => {
    if (!clientId) {
      setIsLoading(false); // No client ID, stop loading
      return;
    }
    
    fetch(`${backendUrl}/api/client-config?client=${clientId}&t=${Date.now()}`, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache'
      }
    })
      .then(res => res.json())
      .then(data => {
        setClientConfig(data);
        setIsLoading(false); // Config loaded, stop loading
      })
      .catch(err => {
        console.error('Error loading client config:', err);
        setIsLoading(false); // Error occurred, stop loading
      });
  }, [clientId]);

  // Force reload config when URL changes
  useEffect(() => {
    const handleUrlChange = () => {
      // Check both URL parameters and route path
      const urlParams = new URLSearchParams(window.location.search);
      let newClientId = urlParams.get("client");
      if (!newClientId) {
        const pathMatch = window.location.pathname.match(/^\/client\/(.+)$/);
        if (pathMatch) {
          newClientId = pathMatch[1];
        }
      }
      
      if (newClientId !== clientId) {
        setClientConfig(null);
        setIsLoading(true);
      }
    };

    window.addEventListener('popstate', handleUrlChange);
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, [clientId]);

  // Manual refresh function to force reload config
  const refreshConfig = () => {
    if (clientId) {
      setClientConfig(null);
      setIsLoading(true);
      fetch(`${backendUrl}/api/client-config?client=${clientId}&t=${Date.now()}&refresh=${Math.random()}`, {
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      })
        .then(res => res.json())
        .then(data => {
          console.log('Refreshed config:', data);
          setClientConfig(data);
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Error refreshing client config:', err);
          setIsLoading(false);
        });
    }
  };

  // Set showTranscript based on config and mode
  useEffect(() => {
    if (previewTranscript) {
      setShowTranscript(true);
      return;
    }

    if (clientConfig) {
      setShowTranscript(getDefaultTranscriptOpenState());
    }
  }, [clientConfig, isWidgetMode, previewTranscript]);

  // Preview-only mode used by iframe guide: show real transcript controls without opening a live call
  useEffect(() => {
    if (!previewTranscript) return;

    const applyPreviewState = () => {
      setIsCalling(true);
      setShowTranscript(true);
      setTranscript([
        { role: "agent", content: "Hello, welcome to Acme Products. How can I help you today?" },
        { role: "user", content: "I want to compare how the transcript fits at different iframe widths." },
        { role: "agent", content: "Perfect. This preview mode uses sample text and does not start a live web call session." },
      ]);
    };

    applyPreviewState();
    const stabilizer = window.setTimeout(applyPreviewState, 180);
    return () => window.clearTimeout(stabilizer);
  }, [previewTranscript]);

  useEffect(() => {
    retellWebClient.on("call_started", () => {
      if (previewTranscript) return;
      setTranscript([]);
      setShowTranscript(getDefaultTranscriptOpenState());
    });
    retellWebClient.on("call_ended", () => {
      if (previewTranscript) return;
      setIsCalling(false);
      setShowTranscript(false);
      setTranscript([]);
    });
    retellWebClient.on("audio", (audio) => {});
    retellWebClient.on("update", (update) => {
      if (update && update.transcript) {
        setTranscript(update.transcript);
      }
    });
    retellWebClient.on("metadata", (metadata) => {});
    retellWebClient.on("error", (error) => {
      if (previewTranscript) return;
      console.error('RetellWebClient error:', error);
      console.error('Error details:', {
        message: error?.message,
        type: error?.type,
        code: error?.code,
        stack: error?.stack
      });
      retellWebClient.stopCall();
    });
  }, [previewTranscript]);

  useEffect(() => {
    const endEl = transcriptEndRef.current;
    if (!endEl) return;

    const container = endEl.closest('.transcript-card') as HTMLElement | null;
    if (!container) return;

    // Auto-follow transcript in widget mode without queueing repeated smooth animations.
    if (isWidgetMode) {
      container.scrollTop = container.scrollHeight;
      return;
    }

    endEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [transcript, isWidgetMode]);

  const [isProcessing, setIsProcessing] = useState(false);

  const buildDynamicVariablesFromUrl = (): DynamicVariablesPayload => {
    const params = new URLSearchParams(window.location.search);
    const dynamicVariables: DynamicVariablesPayload = {
      call_type: params.get("call_type") || "web",
      current_time: new Date().toISOString(),
    };

    const variableMap: Record<string, string> = {
      phone: "user_number",
      context_prompt: "context_prompt",
      notes_body: "notes_body",
      first_name: "first_name",
      last_name: "last_name",
      email: "customer_email",
      company: "user_company_name",
      user_industry: "user_industry",
      offer_voice_ai_demo: "offer_voice_ai_demo",
    };

    Object.entries(variableMap).forEach(([queryKey, variableName]) => {
      const value = params.get(queryKey);
      if (value && value.trim()) {
        dynamicVariables[variableName] = value.trim();
      }
    });

    return dynamicVariables;
  };

  const toggleConversation = async () => {
    // Prevent multiple rapid clicks
    if (isProcessing) {
      return;
    }

    // Clear any previous errors
    setShowError(false);
    setErrorMessage(null);

    if (previewTranscript) {
      // Preview mode: never start or stop real calls
      if (isCalling) {
        setShowTranscript((prev) => !prev);
      } else {
        setIsCalling(true);
        setShowTranscript(true);
      }
      return;
    }

    if (isCalling) {
      setIsCalling(false);
      setTranscript([]);
      retellWebClient.stopCall();
    } else {
      setIsProcessing(true);
      try {
        // Get agentId from client configuration
        let agentId = clientConfig?.agentId;
        
        // For Universal Agent clients, use a placeholder since backend will use environment variable
        if (clientConfig?.accountType === 'universal_agent') {
          agentId = 'universal_agent_placeholder'; // Backend will use SHARED_AGENT_ID
        }
        
        if (!agentId) {
          setErrorMessage('Agent ID not configured. Please contact support.');
          setShowError(true);
          return;
        }
        
        const registerCallResponse = await registerCall(agentId);
        if (registerCallResponse.access_token) {
          setTranscript([]);
          setShowTranscript(getDefaultTranscriptOpenState());
          retellWebClient
            .startCall({
              accessToken: registerCallResponse.access_token,
            })
            .catch((error) => {
              console.error('🔴 Retell call error:', error);
              console.error('Error details:', {
                name: error?.name,
                message: error?.message,
                type: error?.type,
                code: error?.code
              });
              
              // Handle specific permission errors
              if (error?.name === 'NotAllowedError' || error?.message?.includes('Permission denied')) {
                setErrorMessage('Microphone permission denied. Please allow microphone access and try again.');
              } else if (error?.name === 'NotSupportedError') {
                setErrorMessage('Your browser does not support voice calls. Please try a different browser.');
              } else {
                setErrorMessage('Failed to connect to voice AI. Please try again.');
              }
              setShowError(true);
            });
          setIsCalling(true);
        } else {
          setErrorMessage('Failed to get access token. Please try again.');
          setShowError(true);
        }
      } catch (error) {
        console.error('Error starting call:', error);
        
        // Handle specific error messages
        let displayMessage = 'Connection error. Please check your internet and try again.';
        
        if (error instanceof Error) {
          const errorText = error.message.toLowerCase();
          
          if (errorText.includes('api key') || errorText.includes('not configured')) {
            displayMessage = 'API key not configured. Please contact support.';
          } else if (errorText.includes('client not found')) {
            displayMessage = 'Client configuration not found. Please check the client ID.';
          } else if (errorText.includes('server configuration')) {
            displayMessage = 'Server configuration error. Please contact support.';
          } else if (errorText.includes('network')) {
            displayMessage = 'Network connection error. Please check your internet.';
          } else if (errorText.includes('401')) {
            displayMessage = 'Authentication failed. API key may be invalid.';
          } else if (errorText.includes('404')) {
            displayMessage = 'Client not found. Please check the client ID.';
          } else if (errorText.includes('500')) {
            displayMessage = 'Server error. Please try again later.';
          }
        }
        
        setErrorMessage(displayMessage);
        setShowError(true);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  async function registerCall(agentId: string): Promise<RegisterCallResponse> {
    try {
      const dynamicVariables = buildDynamicVariablesFromUrl();
      const response = await fetch(`${backendUrl}/api/create-web-call`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          agent_id: agentId,
          clientId: clientId, // Send clientId so backend can use correct API key
          metadata: {
            source: "web_widget",
            page_url: window.location.href,
          },
          retell_llm_dynamic_variables: dynamicVariables,
        }),
      });
      
      if (!response.ok) {
        // Try to get error details from response
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // If we can't parse the error response, use status-based messages
          if (response.status === 401) {
            errorMessage = 'API key not configured or invalid';
          } else if (response.status === 404) {
            errorMessage = 'Client not found';
          } else if (response.status === 500) {
            errorMessage = 'Server configuration error';
          }
        }
        throw new Error(errorMessage);
      }
      
      const data: RegisterCallResponse = await response.json();
      return data;
    } catch (err) {
      // Re-throw with more context
      if (err instanceof Error) {
        throw err;
      } else {
        throw new Error('Network connection error');
      }
    }
  }

  const roleIcon = (role: string) => {
    if (!showRoleIcons) return null;
    if (role === "user") {
      return <span className="role-icon" title="User" aria-label="User">👤</span>;
    } else if (role === "agent") {
      return <span className="role-icon" title="Agent" aria-label="Agent">🤖</span>;
    }
    return null;
  };

  let iconType = clientConfig?.ui?.icon?.type;
  let iconColor = clientConfig?.ui?.icon?.color || "#fff";
  
  // Memoize the icon element to prevent unnecessary re-renders
  const iconElement: React.ReactNode = useMemo(() => {
    let element: React.ReactNode = null;
    
    if (iconType === "mic") {
      element = <MicIcon color={iconColor} size={iconSize} />;
    } else if (iconType === "classic-mic") {
      element = <ClassicMicIcon color={iconColor} size={iconSize} />;
    } else if (iconType === "headset") {
      element = <HeadsetIcon color={iconColor} size={iconSize} />;
    } else if (iconType === "user") {
      element = <UserIcon color={iconColor} size={iconSize} />;
    } else {
      element = <MicIcon color="#fff" size={iconSize} />;
    }
    
    return element;
  }, [clientConfig?.ui?.icon?.type, clientConfig?.ui?.icon?.color]);

  // Don't render anything until loading is complete (only if we have a client ID)
  if (isLoading && clientId && !isStandaloneRoute) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="*" element={
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <div>Loading...</div>
            </div>
          } />
        </Routes>
      </BrowserRouter>
    );
  }

  // Don't render anything if we don't have client config yet (only if we have a client ID)
  if (!clientConfig && clientId && !isStandaloneRoute) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="*" element={
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
              <div>Loading client configuration...</div>
            </div>
          } />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/iframe-demo" element={<IframeDemo />} />
        <Route path="/client-login" element={<ClientLogin />} />
        <Route path="/client/:clientId" element={<AdminPanel />} />
        <Route path="*" element={
          clientId ? (
            <div
              className="app-container"
              key={`widget-${clientId}-${clientConfig ? JSON.stringify(clientConfig.ui?.icon) : 'loading'}`}
              style={{
                color: clientConfig?.ui?.colors?.primary || "#3a4f90",
                minHeight: "100vh"
              }}
              ref={(el) => {
                if (el) {
                  // For full page mode, use the configured background color
                  // For widget mode, use transparent or default
                  const bgColor = isWidgetMode
                    ? (clientConfig?.ui?.transparentWidgetBg ? "transparent" : "#3a4f90")
                    : (clientConfig?.ui?.colors?.backgroundFull ?? clientConfig?.ui?.colors?.background ?? clientConfig?.ui?.background ?? "#fff");
                  
                  // Set background on both body and app-container for full page
                  if (!isWidgetMode) {
                    document.body.style.setProperty('background', bgColor, 'important');
                    document.documentElement.style.setProperty('background', bgColor, 'important');
                    document.documentElement.style.setProperty('--fullpage-background', bgColor);
                  }
                  el.style.setProperty('background', bgColor, 'important');
                  
                  // Force override any CSS that might be interfering
                  setTimeout(() => {
                    if (!isWidgetMode) {
                      document.body.style.setProperty('background', bgColor, 'important');
                      document.documentElement.style.setProperty('background', bgColor, 'important');
                      document.documentElement.style.setProperty('--fullpage-background', bgColor);
                      el.style.setProperty('background', bgColor, 'important');
                    }
                  }, 100);
                }
              }}
            >
              <header className="header" style={{
                background: getHeadlineBackground()
              }}>
                <div 
                  className="header-title" 
                  style={{
                    color: getTextColor(
                      clientConfig?.ui?.headlineColor || 'primary',
                      clientConfig?.ui?.headlineCustomColor || '#6ec6ff',
                      '#6ec6ff'
                    )
                  }}
                >
                  {clientConfig?.ui?.fullPageHeadline || "Get your (Offer Here)!"}
                </div>
                <div 
                  className="header-sub" 
                  style={{
                    color: getTextColor(
                      clientConfig?.ui?.subheadlineColor || 'secondary',
                      clientConfig?.ui?.subheadlineCustomColor || '#ff6ec4',
                      '#ff6ec4'
                    )
                  }}
                >
                  {clientConfig?.ui?.fullPageSubheadline || "Click to talk to our AI Assistant"}
                </div>
              </header>
              <div className="mic-center">
                <div className="mic-container" style={{ 
                  background: isWidgetMode ? 'transparent' : micCircleBg, 
                  width: micSize, 
                  height: micSize, 
                  borderRadius: '50%', 
                  position: 'relative', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  aspectRatio: '1 / 1'
                }}>
                  {/* Animated Glow */}
                  <div 
                    className="mic-glow"
                    style={{
                      background: `conic-gradient(from 0deg, ${clientConfig?.ui?.colors?.primary || '#3a4f90'}, ${clientConfig?.ui?.colors?.secondary || '#19647e'}, ${clientConfig?.ui?.colors?.primary || '#3a4f90'})`
                    }}
                    data-debug={`primary: ${clientConfig?.ui?.colors?.primary}, secondary: ${clientConfig?.ui?.colors?.secondary}`}
                  ></div>
                  {/* Wave Circles */}
                  <div className={`lines${isCalling ? " active" : ""}`}>
                    <div 
                      className="wave wave1"
                      style={{
                        border: `4px solid ${clientConfig?.ui?.colors?.primary || '#3a4f90'}`
                      }}
                    ></div>
                    <div 
                      className="wave wave2"
                      style={{
                        border: `4px solid ${clientConfig?.ui?.colors?.secondary || '#19647e'}`
                      }}
                    ></div>
                    <div 
                      className="wave wave3"
                      style={{
                        border: `4px solid ${clientConfig?.ui?.colors?.primary || '#3a4f90'}`
                      }}
                    ></div>
                  </div>
                  {/* Mic Button */}
                  <button
                    onClick={toggleConversation}
                    className={isCalling ? "user-btn" : "mic-btn"}
                    aria-label={isCalling ? "Stop Call" : "Start Call"}
                    style={{
                      background: clientConfig?.ui?.buttonGradient 
                        ? `linear-gradient(45deg, ${clientConfig?.ui?.colors?.primary || '#3a4f90'} 0%, ${clientConfig?.ui?.colors?.secondary || '#19647e'} 50%, ${clientConfig?.ui?.colors?.primary || '#3a4f90'} 100%)`
                        : (clientConfig?.ui?.colors?.buttonBg || clientConfig?.ui?.colors?.primary || '#3a4f90'),
                      boxShadow: `0 8px 25px ${clientConfig?.ui?.colors?.primary || '#3a4f90'}4d`
                    }}
                    onMouseEnter={() => {
                      const debugInfo = {
                        buttonGradient: clientConfig?.ui?.buttonGradient,
                        primary: clientConfig?.ui?.colors?.primary,
                        secondary: clientConfig?.ui?.colors?.secondary,
                        buttonBg: clientConfig?.ui?.colors?.buttonBg,
                        finalBackground: clientConfig?.ui?.buttonGradient 
                          ? `linear-gradient(45deg, ${clientConfig?.ui?.colors?.primary || '#3a4f90'} 0%, ${clientConfig?.ui?.colors?.secondary || '#19647e'} 50%, ${clientConfig?.ui?.colors?.primary || '#3a4f90'} 100%)`
                          : (clientConfig?.ui?.colors?.buttonBg || clientConfig?.ui?.colors?.primary || '#3a4f90')
                      };
                      console.log('Button Debug (Hover):', debugInfo);
                      console.log('buttonGradient value:', debugInfo.buttonGradient);
                      console.log('buttonGradient type:', typeof debugInfo.buttonGradient);
                      console.log('finalBackground:', debugInfo.finalBackground);
                    }}
                    onMouseDown={() => {
                      const debugInfo = {
                        buttonGradient: clientConfig?.ui?.buttonGradient,
                        primary: clientConfig?.ui?.colors?.primary,
                        secondary: clientConfig?.ui?.colors?.secondary,
                        buttonBg: clientConfig?.ui?.colors?.buttonBg,
                        finalBackground: clientConfig?.ui?.buttonGradient 
                          ? `linear-gradient(45deg, ${clientConfig?.ui?.colors?.primary || '#3a4f90'} 0%, ${clientConfig?.ui?.colors?.secondary || '#19647e'} 50%, ${clientConfig?.ui?.colors?.primary || '#3a4f90'} 100%)`
                          : (clientConfig?.ui?.colors?.buttonBg || clientConfig?.ui?.colors?.primary || '#3a4f90')
                      };
                      console.log('Button Debug (Click):', debugInfo);
                    }}
                  >
                    {isCalling ? (
                      <img
                        src={`/${clientConfig?.ui?.avatar || "avatar.png"}`}
                        alt="Stop Call"
                        className="user-img"
                        style={{ 
                          width: avatarSize, 
                          height: avatarSize, 
                          borderRadius: '50%',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          aspectRatio: '1 / 1',
                          display: 'block'
                        }}
                      />
                    ) : (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        width: iconSize, 
                        height: iconSize,
                        aspectRatio: '1 / 1'
                      }}>
                        {iconElement}
                      </div>
                    )}
                  </button>
                </div>
                {/* Button Row: Toggle Transcription & End Call */}
                {isCalling && (
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', width: '100%', marginTop: 24, position: 'relative', zIndex: 10 }}>
                    {(() => {
                      const btnStyle = {
                        minWidth: isWidgetMode ? 104 : 120,
                        padding: isWidgetMode ? '7px 10px' : '7px 14px',
                        fontSize: '0.9em',
                        fontWeight: 500,
                        borderRadius: '6px',
                        border: 'none',
                        color: buttonText,
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px 0 #2196f355',
                        transition: 'background 0.2s',
                        display: 'inline-block',
                      };
                      return <>
                        <button
                          style={{
                            ...btnStyle,
                            background: '#3a4f90',
                            position: 'relative',
                            zIndex: 100,
                            pointerEvents: 'auto',
                            transition: 'background 0.2s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#15516a'}
                          onMouseLeave={e => e.currentTarget.style.background = '#3a4f90'}
                          onClick={() => setShowTranscript((prev) => !prev)}
                        >
                          {showTranscript ? 'Hide Text' : 'Show Text'}
                        </button>
                        <button
                          style={{
                            ...btnStyle,
                            background: '#3a4f90',
                            position: 'relative',
                            zIndex: 100,
                            pointerEvents: 'auto',
                            transition: 'background 0.2s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#15516a'}
                          onMouseLeave={e => e.currentTarget.style.background = '#3a4f90'}
                          onClick={() => {
                            if (previewTranscript) {
                              setShowTranscript(false);
                              setIsCalling(false);
                              return;
                            }
                            retellWebClient.stopCall();
                          }}
                        >
                          End Call
                        </button>
                      </>;
                    })()}
                  </div>
                )}
                {/* Transcription Box Below Mic Widget */}
                {showTranscript && isCalling && (
                  <section className="transcript-card" aria-live="polite" style={{ marginTop: 32, marginBottom: 0, background: transcriptBg, color: transcriptText }}>
                    <h3>Transcript</h3>
                    {Array.isArray(transcript) && transcript.length > 0 ? (
                      <>
                        {transcript.map((item, idx) => (
                          <div 
                            key={idx} 
                            className="transcript-entry"
                            style={{
                              borderLeft: `3px solid ${clientConfig?.ui?.colors?.secondary || '#19647e'}`
                            }}
                          >
                            <strong>{roleIcon(item.role)}{item.role}:</strong> {item.content}
                          </div>
                        ))}
                        <div ref={transcriptEndRef} />
                      </>
                    ) : (
                      <span style={{ color: '#888' }}>Transcript will appear here...</span>
                    )}
                  </section>
                )}
                
                {/* Error Message Display */}
                {showError && errorMessage && !hideWarnings && (
                  <div style={{
                    position: 'fixed',
                    bottom: isWidgetMode ? '20px' : '80px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: '#dc3545',
                    color: 'white',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
                    zIndex: 1000,
                    maxWidth: '90%',
                    textAlign: 'center',
                    fontSize: '14px',
                    fontWeight: '500',
                    border: '1px solid #c82333'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                      <span>⚠️</span>
                      <span>{errorMessage}</span>
                      <button 
                        onClick={() => setShowError(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '16px',
                          marginLeft: '8px',
                          padding: '0',
                          lineHeight: '1'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <footer className="footer" style={{
                background: clientConfig?.ui?.colors?.primary || "#3a4f90",
                color: getTextColor(
                  clientConfig?.ui?.footerColor || 'secondary',
                  clientConfig?.ui?.footerCustomColor || '#ffffff',
                  '#ffffff'
                )
              }}>
                {clientConfig?.ui?.fullPageFooter || `© ${new Date().getFullYear()} Powered by Sellflows.com — All rights reserved.`}
              </footer>
            </div>
          ) : (
            <LandingPage />
          )
        } />
      </Routes>
    </BrowserRouter>
  );
};

export default App;












